/** Where the caret sits on an unclamped popover, in px from its left edge (matches workspace.css). */
export const DEFAULT_TETHER = 22;
const MARGIN = 8;
/** Keeps the 12px caret clear of the popover's rounded corners. */
const TETHER_INSET = 14;

export interface PopoverPlacement {
  left: number;
  /** The caret's left offset inside the popover, so it still points at the anchor after clamping. */
  tether: number;
}

/**
 * Keeps a tethered popover inside its container: the preferred left edge is moved in from either
 * side, and the caret slides the other way by the same amount.
 */
export function popoverPlacement(preferredLeft: number, width: number, containerWidth: number): PopoverPlacement {
  const maxLeft = Math.max(MARGIN, containerWidth - width - MARGIN);
  const left = Math.min(Math.max(preferredLeft, MARGIN), maxLeft);
  const tether = Math.min(Math.max(DEFAULT_TETHER + preferredLeft - left, TETHER_INSET), Math.max(TETHER_INSET, width - TETHER_INSET - 12));
  return { left, tether };
}

/**
 * Keeps a popover inside the part of the preview the owner can see. `top` is where it would sit and
 * `height` its full height; `visibleTop`/`visibleBottom` bound what's on screen (all viewport px).
 * Returns how far to move it, and a height limit when it's taller than the space (it then scrolls).
 */
export function keepInView(top: number, height: number, visibleTop: number, visibleBottom: number): { shift: number; maxHeight?: number } {
  const room = Math.max(0, visibleBottom - visibleTop - 2 * MARGIN);
  const fitted = Math.min(height, room);
  const minTop = visibleTop + MARGIN;
  const maxTop = visibleBottom - MARGIN - fitted;
  const shift = top < minTop ? minTop - top : top > maxTop ? maxTop - top : 0;
  return height > room ? { shift, maxHeight: room } : { shift };
}

export type DockCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

/** The corner of the preview away from a point, so a docked popover never covers where the owner tapped. */
export function dockCorner(point: { top: number; left: number }, area: { width: number; height: number }): DockCorner {
  const vertical = point.top > area.height / 2 ? "top" : "bottom";
  const horizontal = point.left > area.width / 2 ? "left" : "right";
  return `${vertical}-${horizontal}`;
}

/** Gap between a popover and what it points at (matches the 10px the popovers use). */
const GAP = 10;

/**
 * A popover opens below what it points at; when it wouldn't fit there but would fit above, it
 * opens above instead. Positions are in the preview area's pixels.
 */
export function opensAbove(anchor: { top: number; bottom: number }, height: number, containerHeight: number): boolean {
  const fitsBelow = anchor.bottom + GAP + height <= containerHeight - MARGIN;
  const fitsAbove = anchor.top - GAP - height >= MARGIN;
  return !fitsBelow && fitsAbove;
}
